// src/pages/TopicsPage.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';

const ALL_TOPICS = [
    { title: "Local Alerts & Safety", description: "Urgent neighborhood warnings and community watch discussions.", count: 5, popular: true },
    { title: "Transportation", description: "Subway status, bus routes, bike lanes, and parking issues.", count: 12, popular: true },
    { title: "Food & Restaurants", description: "Reviews, recommendations, and local spots to try.", count: 8, popular: true },
    { title: "Events & Activities", description: "Upcoming fairs, concerts, and neighborhood gatherings.", count: 15, popular: true },
    // Additional Topics (Initially Hidden)
    { title: "Housing & Real Estate", description: "Rental tips, market trends, and landlord/tenant discussion.", count: 10, popular: false },
    { title: "Civic & Government", description: "Voting, policy changes, and connecting with local officials.", count: 4, popular: false },
    { title: "Small Business Support", description: "Promoting local shops and business collaboration.", count: 9, popular: false },
    { title: "Education & Schools", description: "Discussions about public and private schools in the area.", count: 7, popular: false },
];

const TopicsPage = () => {
    // State to manage whether the full list of topics is visible
    const [showAllTopics, setShowAllTopics] = useState(false);

    // Filter topics based on the state
    const topicsToShow = showAllTopics 
        ? ALL_TOPICS 
        : ALL_TOPICS.filter(topic => topic.popular);

    // Framer Motion Variants
    const container = {
        visible: {
            transition: { staggerChildren: 0.1 }
        }
    };
    
    // Slight modification to item to make it work with re-rendering/expanding list
    const item = {
        hidden: { x: -30, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.4 } }
    };
    
    const TopicBlock = ({ title, description, count }) => (
        <motion.div 
            style={{ padding: '15px', border: '1px solid #eee', borderRadius: '8px', marginBottom: '10px', backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            variants={item}
            whileHover={{ scale: 1.02, boxShadow: '0 4px 10px rgba(0, 31, 63, 0.1)' }}
        >
            <div>
                <h4 style={{ margin: '0 0 5px 0', color: 'var(--color-navy)' }}>{title}</h4>
                <p style={{ margin: '0', fontSize: '0.9rem', color: 'var(--color-gray-medium)' }}>{description}</p>
            </div>
            <span style={{ fontWeight: 'bold', color: 'var(--color-sky)' }}>{count} Chats</span>
        </motion.div>
    );
    
    return (
        <div className="public-page-container">
            <h1 className="public-page-title">Community Topics</h1>
            <p className="public-page-subtitle">Explore the discussions most relevant to your neighborhood and borough.</p>
            
            <motion.div 
                className="page-content-block" 
                style={{ maxWidth: '700px' }}
                variants={container}
                initial="hidden"
                animate="visible"
            >
                <h3 style={{ marginTop: '0' }}>{showAllTopics ? 'All Community Topics' : 'Popular Categories'}</h3>
                
                {/* Map over the filtered/full topics list */}
                {topicsToShow.map((topic, index) => (
                    <TopicBlock 
                        key={index}
                        title={topic.title}
                        description={topic.description}
                        count={topic.count}
                    />
                ))}

                {/* Only show the button if not all topics are currently visible */}
                {!showAllTopics && (
                    <motion.div 
                        style={{ textAlign: 'center', marginTop: '30px' }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1, duration: 0.5 }}
                    >
                        <motion.button 
                            className="auth-button auth-primary-button" 
                            style={{ width: '250px', padding: '12px 0' }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShowAllTopics(true)} // Toggle function
                        >
                            View All Topics
                        </motion.button>
                    </motion.div>
                )}
                
                {/* Optional: Add a button to hide/collapse topics if the list is expanded */}
                {showAllTopics && (
                    <motion.div 
                        style={{ textAlign: 'center', marginTop: '30px' }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1, duration: 0.5 }}
                    >
                        <motion.button 
                            className="auth-button auth-secondary-button" 
                            style={{ width: '250px', padding: '12px 0' }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShowAllTopics(false)}
                        >
                            Show Less
                        </motion.button>
                    </motion.div>
                )}
            </motion.div>
        </div>
    );
};

export default TopicsPage;